import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    getProductLabelStats, getProductLabelVocab, LabelBucket, LabelSource, listProductLabels,
    ProductLabelRow, ProductLabelStats, ProductLabelVocab, VerifyStatus, verifyProductLabel,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import SearchSelect from '../../components/SearchSelect';
import { toast } from '../../utils/toast';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { NoticeBar, SegGroup } from './parts';
import { amazonURL, ProductImg, targetText } from './productLabels';
import ProductLabelCategoryProgress from './ProductLabelCategoryProgress';
import { useProductTargets } from './useProductOptions';

// 商品打标人工审核 (2026-09-15; 2026-09-23 切 asset_product_label_v2; 2026-09-24 面向审核员改版):
// 每条记录上一套 AI 识别结果, 人工逐条裁决, 结果 UPDATE 回 verify_status / final_*.
//
// **这一页是给审核人员用的, 不是给我们自己看数据的。** 2026-09-24 据此砍掉了三样东西:
//   两张并排的卡(官方值 / 模型原值)   合成一张「AI 识别结果」—— 词表映射是实现细节
//   exact / mapped / unmapped 分层   撤掉 —— 审核员看不懂, 也据此决定不了下一步
//   已确认 / 已修正 两档             合显示成「已审核」—— 区别在库里保留, 不上界面
// 换来的是审核员真正要做的判断只剩一个: AI 把这张图读对了没有。
//
// **本页只做亚马逊采集这一条线** (2026-10-09 把 c10s 整支移除): 两个源的粒度与展示
// 字段都不同(图 vs SKU), 混在一个页面里靠 isC10s 分叉, 每加一个功能都要在两支各写一遍。
// 后续 c10s 会做成整页一键切换, 不再是页内分支。后端 source 仍是必填参数, 这里固定传
// product_collect —— 要找 c10s 那支的渲染与筛选, 见本次提交之前的 git 历史。
//
// 为什么不做成表格列表: 两万多条, 表格形态每条都要滚动找位置、点开、回来, 单条成本以秒计;
// 逐条大图 + 键盘裁决把单条成本压到一次按键. 这页的核心是快捷键, 鼠标只是兜底.

// 一次拉一"批"暂存在前端, 页内靠 ← → 走条目, 走到批尾才发下一次请求 —— 每条一次请求会让远程库的 RTT 变成审核节奏.
const PAGE_SIZE = 50;

// 每品类抽审目标量。300 条 = 95% 置信度下可证"错误率 ≤5%"(容许 7 个错);
// 想收到 ≤2% 要 800 条、≤1% 要 1200 条。与品类统计页的 SAMPLE_TARGET 同源。
const SAMPLE_SIZE = 300;

// 抽样种子。**固定常量, 不是"当天日期"** —— 2026-10-09 一度用的是日期, 结果跨天就换一批
// 样本, 昨天审过的今天可能不在样本里, 永远凑不齐一个完整的 300 条。
// 换这个值 = 开启新一轮抽审(之前审过的结论仍在库里, 只是不再属于当前样本集)。
const SAMPLE_SEED = 'label-sample-v1';

// 本页固定的数据源。后端 source 必填(两个源粒度不同, 没有"合起来"的口径)。
const SOURCE: LabelSource = 'product_collect';

// 页面只认四个桶. confirmed / corrected 在库里仍是两个值, 这里一并显示成「已审核」——
// 对审核员而言两者都是"这条审过了", 区别(有没有人工修正值)是下游的事.
const bucketOf = (row: { verifyStatus: string; excluded: boolean }): LabelBucket =>
    (row.excluded ? 'excluded'
        : row.verifyStatus === 'pending' ? 'pending'
            : row.verifyStatus === 'disputed' ? 'disputed' : 'reviewed');

const BUCKET_LABEL: Record<LabelBucket, string> = {
    pending: '待审', reviewed: '已审核', disputed: '无法人工判定', excluded: '已剔除',
};
const BUCKET_CLS: Record<LabelBucket, string> = {
    pending: 'bg-slate-100 text-slate-500', reviewed: 'bg-emerald-50 text-emerald-600',
    disputed: 'bg-amber-50 text-amber-600', excluded: 'bg-rose-50 text-rose-600',
};

// 剔除原因码 → 人话. 码是打标侧给的 (domain/prompts/visibility.py),
// 直接把 other_face_only 摆给审核员看等于没说.
const EXCLUDE_REASON_TEXT: Record<string, string> = {
    other_face_only: '图拍的是商品另一个面（笔记本的屏幕/键盘面、手机正面等）',
    side_view_only: '图拍的是商品侧边、边缘或底面',
    partial_crop: '图是零件级局部特写（接口、按键、纹理、配件）',
    occluded: '主加工面被包装/手/支架挡住了',
    not_a_product_photo: '这张不是商品实拍（信息图/尺寸图/场景图/纯包装）',
    surface_indeterminate: '这类商品没有约定俗成的刻字面',
};

// 详情行: 左标签右值. 宽度 56 是按最长的标签「置信度」定的 —— 44 会让它换行.
function KV({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-baseline gap-2 text-xs">
            <span className="text-slate-400 shrink-0" style={{ width: 56 }}>{label}</span>
            <span className="text-slate-700 min-w-0 break-words">{children}</span>
        </div>
    );
}

// 色块 + 十六进制值. 人工改过就显示改后的, 并标一下 —— 否则改完看不出改没改.
function Swatch({ hex }: { hex: string }) {
    if (!hex) return <span className="text-slate-300">—</span>;
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="shrink-0" style={{ width: 14, height: 14, borderRadius: 3, border: '1px solid #cbd5e1', background: hex }} />
            <span className="font-mono text-[11px] text-slate-500">{hex}</span>
        </span>
    );
}

// 人工改过的值: 原值划掉, 新值跟在后面. 只显示新值的话, "AI 判 X / 人改成 Y"
// 这对差异在界面上就消失了 —— 而复核下一条时恰恰要靠它判断上一条改得对不对.
function Corrected({ from, to }: { from: React.ReactNode; to: React.ReactNode }) {
    return (
        <span className="inline-flex items-baseline gap-1.5 flex-wrap">
            <span className="text-slate-400 line-through decoration-slate-300">{from}</span>
            <span className="text-slate-300">→</span>
            <b className="text-indigo-600">{to}</b>
        </span>
    );
}

function ProductLabelReview() {
    const initQ = new URLSearchParams(useLocation().search);
    // 数据源 = tab. 进 URL 是刻意的: 两个人各认领一个源时, 链接得能直接指过去.
    const source = SOURCE;
    // 默认只看待审: 几千条的工作流是"给我下一条没审的", 其余都是复核时才切.
    // "全部"(空串) 在 URL 里得有个显式值 all —— useSyncQueryToUrl 会把空串直接丢掉,
    // 刷新就又回到默认的待审.
    const [bucket, setBucket] = useState<LabelBucket | ''>(() => {
        // 「全部」那一档已撤, 老链接里的 ?bucket=all 落回待审.
        const q = initQ.get('bucket');
        return (q && q in BUCKET_LABEL ? q as LabelBucket : 'pending');
    });
    const [target, setTarget] = useState(() => initQ.get('target') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(initQ.get('keyword') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    // 逐条审核 / 品类统计. 两者看的是同一批数据的两个面: 一个用来干活, 一个用来判断
    // 哪个品类能放行自动打标.
    const [view, setView] = useState<'review' | 'progress'>(
        () => (initQ.get('view') === 'progress' ? 'progress' : 'review'));
    // 抽审**常开, 且不需要任何前置计算**: 后端直接取当前口径里 CRC32(id‖seed) 最小的
    // SAMPLE_SIZE 行。原先是"取 1/mod", 那个形式要先查总数才能算 mod, 由此多出一个
    // "正在计算抽样比例"的中间态, 还连出两个坑(哨兵与零待审撞车、换筛选时用上一个品类
    // 的 mod)。换成取最小 N 行之后这些全部消失。
    const sampleSize = SAMPLE_SIZE;


    const [list, setList] = useState<ProductLabelRow[]>([]);
    const [total, setTotal] = useState(0);
    const [idx, setIdx] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<ProductLabelStats | null>(null);
    const [vocab, setVocab] = useState<ProductLabelVocab | null>(null);
    // 采集类目下拉的选项**来自数据**(useProductTargets 打 /products/targets), 不来自前端
    // 写死的词表。2026-10-08 踩过: 这里原本是 `Object.entries(TARGET_LABEL)` —— 一份手工
    // 维护的采集侧类目镜像, 采集侧铺到 61 个类目时它还停在 35 个(只有 21 个对得上), 于是
    // **40 个类目在筛选里根本不存在**(hat 19,853 个商品, 审核员没法按它筛)。
    // 关键不是"把缺的补上"而是**筛选项不能依赖手工维护的表**; 词表只负责把 key 显示成中文,
    // 没登记的由 targetText 的 `|| t` 退回显示英文 key —— 退化成"不好看", 不是"筛不到"。
    const targets = useProductTargets()?.targets || [];
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    // 修正态 (按「改一下」进入, 官方词表里没有的行也走这里): 展开表单, 填完才提交.
    // 三项各自独立, 都可留空 = 这一项没改 —— 只想改分类的人不必把材料名色值再抄一遍.
    const [correcting, setCorrecting] = useState(false);
    const [finalL1, setFinalL1] = useState('');
    const [finalL2, setFinalL2] = useState('');
    const [finalMaterialName, setFinalMaterialName] = useState('');
    const [finalColorHex, setFinalColorHex] = useState('');
    const [remark, setRemark] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // 新一批加载完落在首条还是末条: ← 退到上一批时应该停在它的最后一条, 否则会漏看一整批.
    const landRef = useRef<'head' | 'tail'>('head');
    // 刷新页面回到原来的位置 (批内下标只在首次加载时认 URL, 之后翻批一律从头/尾进).
    // 待审筛选下结果集会随审核收缩, 这个下标只保证位置不保证还是同一条 —— 复核用「全部」筛选.
    const initIdxRef = useRef(Number(initQ.get('i')) || 0);
    // 翻批游标: afterIdStack.current[i] = 取第 i+1 批时传给后端的 afterId (第 1 批为 0).
    //
    // **待审队列不能按 offset 翻批**: 它是审一条少一条的, 审完 k 条再取 page+1,
    // offset 指到的已经是原来的第 PAGE_SIZE+k 条, 中间 k 条没审的被整整跳过。
    // 原先为此在待审桶下改成"翻到批尾就重新加载第 1 页", 靠结果集自己收缩来前进 ——
    // 这招只在**每条都审**时成立: 2026-10-12 实测, 审核员翻页浏览不逐条审时收缩量为 0,
    // 重新加载拿回的还是同一批、光标回到第 1 条, 卡在前 50 条里出不去。
    // 钉在 id 上两种用法都对: 审过的行退出结果集不影响 `id > afterId` 的判定。
    //
    // 用栈而不是单个值, 是为了 ← 退回上一批时能拿回那一批的游标。
    // 刷新页面时栈是空的, 退回 offset (后端 afterId=0 时的行为) —— 位置近似, 不跳回第一批。
    const afterIdStack = useRef<number[]>([0]);

    const row: ProductLabelRow | undefined = list[idx];

    // source 是默认值时不写进 URL (urlState 的既定口径: 空串不写).
    useSyncQueryToUrl('/assets/product-labels', {
        source: source === 'product_collect' ? '' : source,
        bucket: bucket === 'pending' ? '' : bucket,
        target,
        keyword,
        // seed 不再进 URL: 它是代码里的固定常量, 不随会话变, 写进去只是噪音.
        view: view === 'progress' ? 'progress' : '',
        page,
        i: idx || '',
    });

    /** 口径: 「要审的是哪一批行」。**刻意不含 bucket** —— 桶是在这批行里面分档,
     *  不是把这批行变小。/stats 正是要在同一批行上把各个桶分别数一遍, 所以它只吃口径。 */
    const scopeParams = useMemo(() => ({
        source,
        // 只给本源那一个: 后端对跨源筛选是 400, 不是静默忽略.
        target: target || undefined,
        keyword: keyword || undefined,
    }), [source, target, keyword]);

    const filterParams = useMemo(() => ({
        ...scopeParams,
        bucket: bucket || undefined,
    }), [scopeParams, bucket]);

    // 抽样参数直接给后端, 前端不再算任何东西.
    //
    // **为什么必须抽, 不能直接审前 N 条**: 列表按 id 排, 而 id 的品类分布极不均 ——
    // 2026-10-08 实测 watch 在低 id 区密度高达 83%, 审前 300 条等于在审手表.
    const sampleParams = { sampleSeed: SAMPLE_SEED, sampleSize };

    const load = useCallback(async () => {
        // **没选类目就不发请求**: 「全部类目」那一档已撤(见类目下拉处的说明),
        // 空 target 是"还没选", 不是"全选"。
        if (!target) { setList([]); setTotal(0); setLoading(false); return; }
        setLoading(true);
        setError('');
        try {
            const res = await listProductLabels({ ...filterParams, ...sampleParams, page, pageSize: PAGE_SIZE,
                afterId: afterIdStack.current[page - 1] || undefined });
            const rows = res.list || [];
            // 总数正好被批大小整除时, 最后一批是满的, 再往后翻就落到空批. 不回退 (回退会把
            // landRef 的 tail 意图冲掉), 只告诉审核员到头了 —— ← 还能退回去。
            if (rows.length === 0 && page > 1) { toast('已经是最后一条了'); }
            setList(rows);
            setTotal(res.total);
            const head = initIdxRef.current || (landRef.current === 'tail' ? rows.length - 1 : 0);
            initIdxRef.current = 0;
            setIdx(Math.min(Math.max(0, head), Math.max(0, rows.length - 1)));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            landRef.current = 'head';
            setLoading(false);
        }
    // 依赖列的是 sampleParams 的两个字段而不是对象本身: 它是每次渲染新建的字面量,
    // 放进依赖会每渲染一次就重新 load 一次, 死循环。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterParams, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { getProductLabelVocab().then(setVocab).catch(() => undefined); }, []);
    // 进度每个源拉一次: 之后每提交一条按状态在本地加减. 每条审完重拉一次统计 = 1 万次远程库聚合查询.
    // 必须跟着 source 走 —— 不然顶部进度条和 SegGroup 上那 7 个计数是两个源的合计,
    // 而列表 total 只有本源的: "待审 27,092" 翻到 10,691 就没了, 谁都会当成 bug 报上来.
    // 统计跟着口径与抽样一起走, 与列表落在**同一批行**上 —— 否则抽审时顶部写着
    // 「待审 47,698」而列表只有 310 条, 审核员会以为自己有 4.7 万条要做.
    //
    // **依赖是 scopeParams 而不是 filterParams**(2026-10-09 修): 带上 bucket 会让每点一次
    // 档位就重发一次 stats, 而第一步 `setStats(null)` 把七个计数整体清空再异步填回 ——
    // 看起来就是数字在一跳一跳。更糟的是**数字会真的变**: 后端 applySampleBy 对
    // `bucket=excluded` 有短路(「已剔除」是只读视图, 不抽样), 于是点到那一档时整个抽样
    // 被跳过, 七个计数从样本内口径(几百)跳成全量口径(几万)。
    // 桶本来就不该进 /stats —— 它要数的就是同一批行里各档各有多少。
    useEffect(() => {
        if (!target) { setStats(null); return; }
        setStats(null);
        getProductLabelStats({ ...scopeParams, ...sampleParams }).then(setStats).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeParams]);

    // 预热下一条的商品图: 每条等半秒加载, 1 万条就是一个半小时.
    useEffect(() => {
        const next = list[idx + 1];
        if (next?.imageUrl) new Image().src = next.imageUrl;
    }, [list, idx]);

    // 换条目时草稿回到该条的现状 (已审条目可直接改判, 不用重敲一遍).
    useEffect(() => {
        setCorrecting(false);
        setFinalL1(row?.finalL1 || '');
        setFinalL2(row?.finalL2 || '');
        setFinalMaterialName(row?.finalMaterialName || '');
        setFinalColorHex(row?.finalColorHex || '');
        setRemark(row?.remark || '');
    }, [row?.id, row?.finalL1, row?.finalL2, row?.finalMaterialName, row?.finalColorHex, row?.remark]);

    // 换筛选 = 换一批行, 旧游标指向的 id 在新口径里没有意义, 必须连栈一起清。
    const resetToFirstPage = () => { landRef.current = 'head'; afterIdStack.current = [0]; setPage(1); setIdx(0); };


    // 当前条在结果集里的序号. 每批都是满 PAGE_SIZE 条 (不满就没有下一批了), 所以
    // 按页码算仍然成立 —— 游标只换了"怎么取下一批", 没换"这是第几批".
    const cursor = (page - 1) * PAGE_SIZE + idx + 1;

    // 走到批尾/批头才换批.
    //
    // **按游标翻, 不按页码**(2026-10-12 改): 记下本批最后一条的 id, 下一批取 id 比它大的。
    // 待审队列审一条少一条, 页码对应的位置会随之前移, 而 id 不会 —— 理由与取舍见 afterIdStack。
    //
    // 本批不满 PAGE_SIZE 就是取完了 (后端 LIMIT 到头才会给不满的一批), 这个判断比 totalPages
    // 可靠: 待审桶下 total 随审核缩小, 按它算的"最后一页"会提前到达, 把还没审的挡在后面。
    const nextBatch = () => {
        const last = list[list.length - 1];
        if (!last || list.length < PAGE_SIZE) { toast('已经是最后一条了'); return; }
        afterIdStack.current[page] = last.id;
        landRef.current = 'head';
        setPage(page + 1);
    };
    const prevBatch = () => {
        if (page <= 1) { toast('已经是第一条了'); return; }
        landRef.current = 'tail';
        setPage(page - 1);
    };
    const go = (step: 1 | -1) => {
        const next = idx + step;
        if (next >= 0 && next < list.length) { setIdx(next); return; }
        if (step === 1) nextBatch(); else prevBatch();
    };

    // 本地加减进度计数, 口径与 /stats 一致 (四个桶互斥).
    // **按桶加减而不是按 verify_status**: confirmed→corrected 这种改判在页面上没有位移
    // (两者都是「已审核」), 照状态加减会把 reviewed 减一再加一以外的格子也动到.
    const bumpStats = (fromBucket: LabelBucket, toBucket: LabelBucket) => setStats((prev) => {
        if (!prev || fromBucket === toBucket) return prev;
        // excluded 那一格不参与加减: 剔除是打标侧判的, 审核动作改不了它,
        // 而它与另外三格无交集 —— 动它只会让"三格之和 = total"这条不变式失真.
        const live = (b: LabelBucket): b is 'pending' | 'reviewed' | 'disputed' => b !== 'excluded';
        const next = { ...prev };
        if (live(fromBucket)) next[fromBucket] = Math.max(0, next[fromBucket] - 1);
        if (live(toBucket)) next[toBucket] += 1;
        return next;
    });

    const submit = async (status: VerifyStatus,
        finals: { l1?: string; l2?: string; materialName?: string; colorHex?: string }) => {
        // 剔除行只读: 后端 base() 把它挡在审核面外, 提交会换来一句"记录不存在".
        // 在这里先拦一道, 省得审核员对着一条永远提交不上去的行反复按.
        if (!row || submitting || row.excluded) return;
        setSubmitting(true);
        try {
            await verifyProductLabel(row.id, {
                verifyStatus: status,
                finalL1: finals.l1 || '', finalL2: finals.l2 || '',
                finalMaterialName: finals.materialName || '', finalColorHex: finals.colorHex || '',
                remark: remark.trim(),
            });
            const from = bucketOf(row);
            // 不重拉列表: 待审筛选下重拉会让整个窗口位移, 正在审的上下文当场跳走. 本地改这一行即可,
            // 后端在非 corrected 时会清空 final*, 这里跟着清, 免得界面显示一个库里不存在的值.
            const patched: ProductLabelRow = {
                ...row, verifyStatus: status, remark: remark.trim(),
                finalL1: status === 'corrected' ? finals.l1 || '' : '',
                finalL2: status === 'corrected' ? finals.l2 || '' : '',
                finalMaterialName: status === 'corrected' ? finals.materialName || '' : '',
                finalColorHex: status === 'corrected' ? finals.colorHex || '' : '',
            };
            setList((prev) => prev.map((r) => (r.id === row.id ? patched : r)));
            bumpStats(from, bucketOf(patched));
            setCorrecting(false);
            go(1);
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setSubmitting(false);
        }
    };

    const taxonomy = useMemo(() => vocab?.taxonomy || [], [vocab]);
    const l2Options = useMemo(
        () => taxonomy.find((t) => t.l1 === finalL1)?.l2 || [],
        [taxonomy, finalL1],
    );
    // 修正面板一二级材质下拉: 显示中文 (与英文不同才补英文), 值是英文
    const l1SelectOpts = useMemo(
        () => taxonomy.map((t) => ({
            value: t.l1,
            label: `${t.l1Zh || t.l1}${t.l1Zh && t.l1Zh !== t.l1 ? ` (${t.l1})` : ''}`,
        })),
        [taxonomy],
    );
    const l2SelectOpts = useMemo(
        () => l2Options.map((l2) => ({
            value: l2.en,
            label: `${l2.zh || l2.en}${l2.zh && l2.zh !== l2.en ? ` (${l2.en})` : ''}`,
        })),
        [l2Options],
    );
    // 官方英文值 → 中文名。由 vocab 现算, **不在前端硬编码** —— 硬编码那张表迟早与
    // material_category 漂移, 而漂移的表现是页面显示一个谁也没配过的中文名。
    // 词表里没有的值(分类被停用、或 AI 给的原值落在词表外)回退显示英文, 不显示空白。
    const zhOf = useMemo(() => {
        const m = new Map<string, string>();
        for (const t of taxonomy) {
            if (t.l1Zh) m.set(t.l1, t.l1Zh);
            for (const l2 of t.l2) if (l2.zh) m.set(l2.en, l2.zh);
        }
        return (en: string) => (en ? m.get(en) || en : '');
    }, [taxonomy]);
    // 预填 AI 的现值: 改分类的人多半只改分类, 另外两项原样带上省得他重抄.
    // 分类只预填落在官方词表里的值 —— 词表外的原值填进下拉会选不中, 反而更费事.
    const enterCorrect = () => {
        setFinalL1(ai?.l1 || '');
        setFinalL2(ai?.l2 || '');
        // 预填中文而不是英文原文: 卡片上显示的是中文, 点「改一下」跳出英文会让人以为
        // 改的不是刚才看的那条。他想写英文就自己改, 反正这一列是自由文本。
        setFinalMaterialName(row?.finalMaterialName || ai?.materialNameZh || ai?.materialName || '');
        setFinalColorHex(row?.finalColorHex || ai?.colorHex || '');
        setCorrecting(true);
    };
    const submitCorrection = () => {
        if (!finalL1 || !finalL2) { toast('请先选完一级和二级分类'); return; }
        const hex = finalColorHex.trim();
        // 前端先拦一道: 后端按 ^#[0-9A-Fa-f]{6}$ 拒, 但那是一句英文报错弹在页面角上,
        // 审核员正在连按快捷键, 很容易没看见就以为提交成功了.
        if (hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) { toast('颜色要填 #RRGGBB, 例如 #2E3036'); return; }
        submit('corrected', {
            l1: finalL1, l2: finalL2,
            materialName: finalMaterialName.trim(), colorHex: hex,
        });
    };

    const ai = row?.ai;
    // 剔除行一律只读: 它已被剔出审核面, 提交必然失败.
    const readOnly = Boolean(row?.excluded);
    // 「AI 判对」还要求两级官方值都在: 官方词表里没有对应项时二级是空的, 确认它等于
    // 确认一个空分类 —— 那条行的正事恰恰是让人补上, 所以禁掉, 逼它走「改一下」。
    const canConfirm = !readOnly && Boolean(ai?.l1 && ai?.l2);
    // AI 原值与人工改过的值: 页面各处都要用, 在这里算一次.
    const shownL1 = row?.finalL1 || ai?.l1 || '';
    const shownL2 = row?.finalL2 || ai?.l2 || '';
    const isCorrected = row?.verifyStatus === 'corrected';

    // 每次渲染重建 (5 个对象, 便宜): 里面的回调闭包的是当前条目与草稿, 记忆化只会拿到上一条的状态.
    //
    // 「无法人工判定」落库仍是 verify_status='disputed' (只换文案, 不动存储值).
    // 它必须和 confirmed 分开: 混进去就再也分不出"人确认了 AI 判对"和"人根本看不出来",
    // 而后者正是回头调置信度阈值时必须先摘掉的样本.
    // 「改一下」预填 AI 的一级: unmapped 行的一级是有效官方值(是金属, 只是官方无镁),
    // 预填省掉一次选择; 一级本身也落不到词表时预填空串, 让人从头选。
    const actions = [
        { key: '1', label: 'AI 判对', desc: '确认这条判定', cls: 'border-emerald-200 hover:bg-emerald-50', enabled: canConfirm, run: () => submit('confirmed', {}) },
        { key: '2', label: '改一下', desc: '改分类 / 材料名 / 颜色', cls: 'border-rose-200 hover:bg-rose-50', enabled: !readOnly, run: enterCorrect },
        { key: '3', label: '无法人工判定', desc: '看不出材质', cls: 'border-amber-200 hover:bg-amber-50', enabled: !readOnly, run: () => submit('disputed', {}) },
    ];

    // 快捷键: 1/2/3 裁决 · ← → 上下条 · Enter 提交修正.
    // 1/3 不需要补录信息, 按下即提交并跳下一条 —— 这页的吞吐全靠这一下; 按错了 ← 回去重审即可 (UPDATE 幂等).
    // 回调依赖当前条目和草稿, 放 ref 里让监听只绑一次, 免得每换一条重绑一次 document.
    const onKey = (e: KeyboardEvent) => {
        if (zoomSrc) return; // 放大态归 ImageLightbox (Esc 关闭)
        const el = e.target as HTMLElement | null;
        // 修正面板整体算输入态: 可搜索下拉的触发器是 button, 不算进来的话选完分类敲数字会被当成裁决
        const typing = Boolean(el?.closest?.('input,textarea,select,[data-review-form]'));
        if (typing) {
            // 在输入框里不劫持按键 (敲备注打个 "1" 就提交了); 修正面板里的 Enter 仍然是提交.
            if (e.key === 'Enter' && el?.closest?.('[data-review-form]')) { e.preventDefault(); submitCorrection(); }
            return;
        }
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (correcting) submitCorrection(); else toast('按 1/2/3 裁决, 修正态下 Enter 提交');
            return;
        }
        const act = actions.find((a) => a.key === e.key);
        if (!act) return;
        e.preventDefault();
        if (!act.enabled) { toast(`这条不能选「${act.label}」`); return; }
        act.run();
    };
    const keyRef = useRef(onKey);
    useEffect(() => { keyRef.current = onKey; });
    useEffect(() => {
        const h = (e: KeyboardEvent) => keyRef.current(e);
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, []);

    const done = stats ? stats.reviewed + stats.disputed : 0;
    const donePct = stats && stats.total > 0 ? (done / stats.total) * 100 : 0;
    // 顶部是一条漏斗: 总商品图 → 已打标 → 其中剔除了多少。
    //
    // **三个数来自两张表**: imageTotal 是采集表 image_file 里可打标的图(后端另查一条,
    // 与打标侧待办准入同口径); labeled 与 excluded 来自结果表。所以
    //   imageTotal - labeled = 还没打标
    //   labeled  - excluded = 进审核面(= stats.total, 下面进度条的分母)
    //
    // 剔除比例的分母取**已打标**而不是总图数: 没打标的图还没被判过可识别性,
    // 把它们算进分母会让比例随打标进度漂移 —— 同一批数据昨天 30% 今天 32%, 而其实
    // 什么都没变。
    const labeled = stats ? stats.total + stats.excluded : 0;
    const imagesTotal = stats?.imageTotal ?? 0;
    const excludedPct = labeled > 0 ? ((stats?.excluded ?? 0) / labeled) * 100 : 0;
    const labeledPct = imagesTotal > 0 ? (labeled / imagesTotal) * 100 : 0;

    return (
        <div className="space-y-4">
            {/* 页头 + 进度 */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Product Label · Human Review</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">商品打标人工审核</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        亚马逊采集商品图的 AI 识别结果逐条裁决 —— 判对就确认，判错就改分类 / 材料名 / 颜色，看不出材质的标「无法人工判定」。
                    </div>
                    {/* 粒度说明放页头而不是 tab 旁边: 它描述的是整页数据的口径,
                        与当前在看哪个 tab 无关 —— 挂在 tab 边上会被读成那个 tab 的注脚。 */}
                    <div className="text-xs text-slate-400 mt-1">
                        图粒度：一个 ASIN 的多张图各判各的
                    </div>
                </div>
                {/* 宽度给到 360 且允许折行: 「无法人工判定」比原来的「存疑」宽 4 个字,
                    四个 chip 在 300 里会向左溢出压到左边的描述文字上 */}
                <div className="text-right shrink-0" style={{ width: 360 }}>
                    {/* 漏斗行: 总商品图 → 已打标 → 剔除。下面那行的分母是**审核面**
                        (已扣掉剔除), 与这里任何一个数都不同 —— 不摆出来的话, 审核员看到
                        7,568 会以为图就这么多。c10s 没有图全集, 那一段整体不显示。 */}
                    <div className="flex items-baseline gap-x-2 gap-y-0.5 justify-end flex-wrap text-xs">
                        {imagesTotal > 0 && (
                            <>
                                <span className="text-slate-400">总商品图</span>
                                <b className="text-sm font-bold text-slate-700 tabular-nums">{imagesTotal.toLocaleString()}</b>
                                <span className="text-slate-400" title="已产出 AI 判定结果的图 (含被剔除的)">
                                    已打标 <b className="text-slate-600 tabular-nums">{labeled.toLocaleString()}</b>
                                    <span className="text-slate-400 tabular-nums"> ({labeledPct.toFixed(1)}%)</span>
                                </span>
                            </>
                        )}
                        <span className={imagesTotal > 0 ? 'border-l border-slate-200 pl-2 text-slate-400' : 'text-slate-400'}
                              title="主加工面在图上认不出来(只拍到侧边/键盘面/局部特写等), 已剔出审核面, 不进下游。比例的分母是「已打标」——没打标的图还没被判过可识别性">
                            已剔除 <b className="text-rose-500 tabular-nums">{stats?.excluded.toLocaleString() ?? '—'}</b>
                            <span className="text-rose-400 tabular-nums"> ({excludedPct.toFixed(1)}%)</span>
                        </span>
                    </div>
                    <div className="flex items-baseline gap-2 justify-end text-xs mt-0.5">
                        <span className="text-slate-400">已审</span>
                        <b className="text-lg font-extrabold text-slate-800 tabular-nums">{done.toLocaleString()}</b>
                        <span className="text-slate-400">/ {stats ? stats.total.toLocaleString() : '—'}</span>
                        <span className="font-semibold text-emerald-600 tabular-nums">{donePct.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded overflow-hidden mt-1.5">
                        <div className="h-full bg-emerald-500 rounded" style={{ width: `${donePct}%` }} />
                    </div>
                    <div className="flex flex-wrap justify-end gap-x-3 gap-y-1 text-[11px] text-slate-400 mt-1.5">
                        <span>已审核(可用) <b className="text-emerald-600">{stats?.reviewed.toLocaleString() ?? '—'}</b></span>
                        <span>无法人工判定 <b className="text-amber-600">{stats?.disputed.toLocaleString() ?? '—'}</b></span>
                        <span>待审 <b className="text-slate-600">{stats?.pending.toLocaleString() ?? '—'}</b></span>
                        {/* 「已剔除」不在这一组: 这三项之和 = 上面那行的分母(审核面),
                            而剔除不属于它 —— 已挪到顶部总量行, 与总数并排。 */}
                    </div>
                </div>
            </div>

            {/* 这一行 tab 表示的是**视图**, 不是数据源。
                c10s 下掉之后源只剩亚马逊采集一个, 再把它摆成 tab 会出两个毛病:
                它的选中态恒为真(source === key 永远成立), 于是切到「品类统计」时两个
                tab 同时高亮; 而点它只调 switchSource、不动 view, 点了没反应。
                —— 2026-10-09 实际踩到。一行 tab 只能表示一个轴。 */}
            <div className="flex items-center gap-1 border-b border-slate-100">
                {([['review', '人工抽检'], ['progress', '品类统计']] as const).map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setView(key)}
                        className={`px-4 py-2 text-sm -mb-px border-b-2 transition ${view === key
                            ? 'border-slate-800 text-slate-900 font-semibold'
                            : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
                        {label}
                    </button>
                ))}
                <span className="ml-3 text-[11px] text-slate-400">
                    {view === 'progress'
                        ? '按采集类目看审核进展与修正率，判断哪些品类可以放行自动打标'
                        : '只放随机抽出的样本，用来估各品类的打标准确率'}
                </span>
            </div>

            {/* 品类统计: 独立一屏, 不与审核界面混排 —— 它不是筛选出来的一个视图,
                是另一个问题(哪些品类能放行)的答案。 */}
            {/* 抽样参数必须与本页一致, 否则统计页会退回全量口径, 两个 tab 的数对不上.
                2026-10-10 踩过: 统计页已审 269(全量) / 抽检页待审 189(样本内). */}
            {view === 'progress' && (
                <ProductLabelCategoryProgress source={source}
                    sampleSeed={sampleParams.sampleSeed} sampleSize={sampleParams.sampleSize} />
            )}

            {view === 'review' && <>
            {/* 筛选 */}
            <div className="card px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                {/* 顺序: 全部 → 待审 → 已审核 → 无法人工判定 → 已剔除。
                    「全部」打头是总量锚点 —— 先看清盘子多大, 再看各档占多少;
                    「已剔除」仍压在最后且只读: 它不是待办, 是给人查的, 而且**不计入全部**
                    (total 的定义就排除了它), 夹在中间会被读成同一组求和里的一档。
                    默认选中的仍是「待审」而不是「全部」: 审核员打开页面就该落在待办上,
                    位置是阅读顺序, 默认值是工作流, 两者不必一致。
                    「全部」的计数用 stats.total 而不是另算一个数: total 的定义就是
                    **审核面总数(不含已剔除)**, 而「全部」这一档列的正是同一批行 ——
                    两边各算各的迟早对不上, 而"标签上的数与翻页翻到的条数不符"是最招人
                    怀疑数据出问题的那种不一致。
                    「已审核」标成「可用」: 这一桶是 confirmed + corrected 且未被剔除,
                    也就是真正能进下游的那批 —— 审核员问的"我审出来多少能用的"就是它。 */}
                <SegGroup label="" value={bucket} onChange={(v) => { setBucket(v as LabelBucket | ''); resetToFirstPage(); }}
                    options={[
                        // **没有「全部」这一档**(2026-10-10 撤掉): 本页只放抽出来的样本,
                        // 「全部」在这里是"样本里的全部"(三百多条), 和页面其它地方的"全部"
                        // 不是一个意思 —— 摆着只会被读成总量。要看全量去「品类统计」。
                        { value: 'pending', label: `待审${stats ? ` ${stats.pending.toLocaleString()}` : ''}` },
                        { value: 'reviewed', label: `已审核(可用)${stats ? ` ${stats.reviewed.toLocaleString()}` : ''}` },
                        { value: 'disputed', label: `无法人工判定${stats ? ` ${stats.disputed.toLocaleString()}` : ''}` },
                        { value: 'excluded', label: `已剔除${stats ? ` ${stats.excluded.toLocaleString()}` : ''}` },
                    ]} />
                {/* **没有「全部采集类目」这一档**(2026-10-09 撤掉)。后端列表的抽样是全局的
                    (applyScope → applySample, 不分区), 所以"全部"抽出来的 300 条是在五万多条
                    审核面上**按量摊**的 —— 实测 watch 一个品类就占掉约 111 条, 而
                    fountain_pen / keychain / notebook / coaster / award / pencil / metal_pen /
                    usb_drive / pen / ornament / magnet 这 11 个品类的期望值全部不到 1 条。
                    页面上只会显示成"这些品类还没人审", 看不出是抽样根本没覆盖到。
                    而「品类统计」是按品类分区抽的(applySampleBy + target), 两边口径对不上。

                    放行判据本来就是逐品类的, 派活也是逐品类分给不同审核员(链接直接带
                    ?target=watch), 所以这里要求必须选一个。空 target = "还没选", 不是"全选"。 */}
                {/* 可搜索下拉而不是原生 select: 采集侧已经铺到 56 个有图的类目, 一个
                    五十多项的原生下拉要靠肉眼翻 —— 而审核员每次开工都要先在这里定位
                    自己被分到的那个。键入中文名或英文 key 都能过滤。 */}
                <SearchSelect className="w-40" placeholder="选择采集类目…"
                    value={target}
                    options={targets.map((t) => ({ value: t.key, label: targetText(t.key) }))}
                    onChange={(v) => { setTarget(v); resetToFirstPage(); }} />
                <input className="inp" style={{ width: 200 }} placeholder="商品名关键词…" value={keywordDraft}
                    onChange={(e) => setKeywordDraft(e.target.value)}
                    onBlur={() => { if (keywordDraft.trim() !== keyword) { setKeyword(keywordDraft.trim()); resetToFirstPage(); } }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setKeyword(keywordDraft.trim()); resetToFirstPage(); } }} />
                {/* 抽审开关. 种子固定成当天日期: 同一天内刷新/换机器/把链接发给别人, 圈出的
                    都是同一批行 —— 样本集要可复现, 否则事后算不出准确率代表什么。 */}
                <div className="ml-auto text-xs text-slate-400 tabular-nums">
                    第 <b className="text-slate-700">{total > 0 ? cursor.toLocaleString() : 0}</b> / {total.toLocaleString()} 条
                </div>
            </div>

            {/* 抽审状态必须显式告诉审核员: 否则"列表只剩 300 条"和"这个品类只有 300 条待审"
                在界面上长得一模一样, 而两者对应完全不同的结论。 */}
            <div className="card p-2 text-xs text-indigo-700 border-indigo-200 bg-indigo-50">
                随机抽查：本页只放当前筛选里随机抽出的 <b>{sampleSize}</b> 条（不足则全给），
                顶部各档计数同样只统计这批。<b>未抽中的条目仍在库里</b>，只是本轮不审。
                切换采集类目可换一批。（种子 <code className="font-mono">{SAMPLE_SEED}</code>，
                同一种子永远抽到同一批，换种子 = 开启新一轮抽审）
            </div>

            {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}

            {!target ? (
                <div className="card p-12 text-center text-sm text-slate-500">
                    请先在上方选择一个<b>采集类目</b>
                    <div className="text-xs text-slate-400 mt-2 leading-relaxed">
                        抽审是<b>逐品类</b>各抽各的 {sampleSize} 条 —— 放行判据按品类算，派活也按品类分。<br />
                        所以这里没有「全部类目」：跨品类抽出来的样本会被大品类占满，小品类一条都抽不到。
                    </div>
                </div>
            ) : loading && !row ? (
                <div className="card p-12 text-center text-sm text-slate-400">加载中…</div>
            ) : !row ? (
                <div className="card p-12 text-center text-sm text-slate-400">
                    当前筛选下没有待审条目
                    {bucket === 'pending' && <div className="text-xs mt-2">这一批抽样都审完了。切到「已审核」可以复核，或换一个采集类目继续。</div>}
                </div>
            ) : (
                <div className="grid gap-4 items-start" style={{ gridTemplateColumns: '460px 1fr' }}>
                    {/* 左: 商品大图 */}
                    <div className="card p-3">
                        {row.imageUrl ? (
                            <ProductImg src={row.imageUrl} className="w-full rounded-lg object-contain bg-white border border-slate-100 cursor-zoom-in"
                                style={{ aspectRatio: '1 / 1' }} title="点击放大" onClick={() => setZoomSrc(row.imageUrl)} />
                        ) : (
                            <div className="w-full rounded-lg bg-slate-50 flex items-center justify-center text-xs text-slate-400" style={{ aspectRatio: '1 / 1' }}>没有商品图</div>
                        )}
                        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
                            <span className="font-mono truncate" title={row.sourceId}>{row.sourceId}</span>
                            <span className="ml-auto whitespace-nowrap">#{row.id}</span>
                        </div>
                    </div>

                    {/* 右: 商品信息 / AI 判定 / 裁决 */}
                    <div className="space-y-3">
                        <div className="card p-4">
                            <div className="flex items-start gap-3">
                                <div className="min-w-0">
                                    <div className="text-[15px] font-bold text-slate-900 leading-snug break-words">{row.title || <span className="text-slate-400">无标题</span>}</div>
                                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 mt-1.5">
                                        <span className="font-semibold text-slate-600">{row.brand || '未知品牌'}</span>
                                            <span>·</span><span>{targetText(row.target)}</span>
                                            <span>·</span>
                                            <a href={amazonURL(row.asin)} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline">{row.asin} ↗</a>
                                    </div>
                                </div>
                                <div className="ml-auto flex items-center gap-2 shrink-0">
                                    <span className={`tag ${BUCKET_CLS[bucketOf(row)]}`}>{BUCKET_LABEL[bucketOf(row)]}</span>
                                </div>
                            </div>
                        </div>

                        {/* 一张卡: AI 识别出的那一整组值。
                            2026-09-24 从「官方分类 + 模型原值」两张并排合过来 —— 并排是为了让人判断
                            词表映射对不对, 而那是我们的实现细节, 不是审核员要做的判断。他要判的只有
                            一件事: AI 把这张图读对了没有。
                            分类主体显示**落库的官方值**(人点「判对」时看到的就是将要存进库的结论),
                            半开放原值跟在后面做副标 —— 信息不丢, 但不再占一张卡。 */}
                        <div className="card p-3 space-y-1.5">
                            <div className="flex items-baseline gap-2 mb-1">
                                <div className="text-xs font-bold text-slate-700">AI 识别结果</div>
                                {isCorrected && <div className="text-[10px] text-indigo-500">已人工修正</div>}
                                <div className="ml-auto text-[11px] text-slate-400">
                                    置信度 <span className="font-mono text-slate-500">{ai?.confidence != null ? ai.confidence.toFixed(2) : '—'}</span>
                                </div>
                            </div>

                            {/* 中文为主、英文小字跟后。**存储一律英文** —— 中文只是 material_category
                                的 name_zh, 下游对的是 material.type1/type2 那套英文值。
                                英文不省掉: 排查时页面上就得能看到落库的实际值, 否则一有问题
                                就要回库里查"这条到底存的是什么"。 */}
                            <KV label="分类">
                                {shownL1 || shownL2 ? (
                                    <span className="inline-flex items-baseline gap-1.5 flex-wrap">
                                        {isCorrected && row.finalL1 ? (
                                            <Corrected from={`${zhOf(ai?.l1 || '') || '—'} / ${zhOf(ai?.l2 || '') || '—'}`}
                                                to={`${zhOf(shownL1) || '—'} / ${zhOf(shownL2) || '—'}`} />
                                        ) : (
                                            <b className="text-slate-800">{zhOf(shownL1)} / {zhOf(shownL2)}</b>
                                        )}
                                        <span className="text-[11px] text-slate-400 font-mono">{shownL1} / {shownL2}</span>
                                        {/* 原值与官方值不同时才给副标: 相同还显示等于把同一个词说两遍。
                                            原值是 prism 的半开放输出, 没有中文可对, 保持英文。 */}
                                        {!isCorrected && ai?.l2Raw && ai.l2Raw.trim().toLowerCase() !== (ai.l2 || '').toLowerCase() && (
                                            <span className="text-[11px] text-slate-400">· 原值 {ai.l1Raw} / {ai.l2Raw}</span>
                                        )}
                                    </span>
                                ) : (
                                    <span className="text-rose-600">
                                        {ai?.l1Raw || ai?.l2Raw ? `${ai.l1Raw || '—'} / ${ai.l2Raw || '—'}` : '—'}
                                        <span className="ml-1.5 text-[11px]">官方分类里没有这一项，请按「改一下」手选</span>
                                    </span>
                                )}
                            </KV>
                            {/* 同上: 中文是 material_name 译得的派生值(materialNameZh),
                                英文原文跟在后面。人工改过就只显示人填的那串 —— 他填什么语言是他的事。 */}
                            <KV label="材料名">
                                {isCorrected && row.finalMaterialName ? (
                                    <Corrected from={ai?.materialNameZh || ai?.materialName || '—'}
                                        to={row.finalMaterialName} />
                                ) : row.finalMaterialName ? row.finalMaterialName : (ai?.materialName ? (
                                    <span className="inline-flex items-baseline gap-1.5 flex-wrap">
                                        <span>{ai.materialNameZh || ai.materialName}</span>
                                        {ai.materialNameZh && (
                                            <span className="text-[11px] text-slate-400 font-mono">{ai.materialName}</span>
                                        )}
                                    </span>
                                ) : <span className="text-slate-300">—</span>)}
                            </KV>
                            <KV label="颜色">
                                {isCorrected && row.finalColorHex
                                    ? <Corrected from={<Swatch hex={ai?.colorHex || ''} />} to={<Swatch hex={row.finalColorHex} />} />
                                    : <Swatch hex={row.finalColorHex || ai?.colorHex || ''} />}
                            </KV>
                            <KV label="工艺">{ai?.surfaceProcess || <span className="text-slate-300">—</span>}</KV>
                        </div>

                        {/* 剔除行: 说清为什么剔, 并挑明它只能看不能改 —— 否则审核员会对着
                            一排灰掉的按钮反复点, 以为是页面坏了。 */}
                        {row.excluded && (
                            <div className="card px-3 py-2 text-[11px] text-rose-600 bg-rose-50 border-rose-200 leading-relaxed">
                                <b>这张图已剔除</b>：{EXCLUDE_REASON_TEXT[row.excludeReason] || row.excludeReason || '主加工面在图上认不出来'}。
                                <span className="text-rose-400"> 这批只供查看，不用审。</span>
                            </div>
                        )}


                        {!row.excluded && row.verifyStatus !== 'pending' && !correcting && (
                            <NoticeBar>
                                这条已审过（{BUCKET_LABEL[bucketOf(row)]}
                                {row.remark ? ` · ${row.remark}` : ''}）。再按一次按钮即改判。
                            </NoticeBar>
                        )}

                        {/* 裁决 */}
                        <div className="card p-3 space-y-3">
                            {/* 3 列 —— 2026-09-23 随单段模型从 5 档收成 3 档时一并改.
                                列数必须跟着 actions 的条数走: 留着 grid-cols-5 的话三个按钮会挤在
                                左边 3/5、右边空一大块 (实测过)。
                                每格文本区因此宽裕了, 原来为「无法人工判定」6 字撑破 100px 而收窄的
                                px-2 + gap-1.5 保留即可, 不必再放宽。 */}
                            <div className="grid grid-cols-3 gap-1.5">
                                {actions.map((a) => (
                                    <button key={a.key} type="button" disabled={!a.enabled || submitting}
                                        className={`rounded-xl border px-2 py-2.5 text-left bg-white ${a.cls} ${!a.enabled || submitting ? 'opacity-40 cursor-not-allowed' : ''}`}
                                        onClick={a.run}>
                                        <div className="flex items-center gap-1.5">
                                            <span className="w-5 h-5 rounded bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{a.key}</span>
                                            <span className="text-[13px] font-bold text-slate-800 whitespace-nowrap">{a.label}</span>
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-1">{a.desc}</div>
                                    </button>
                                ))}
                            </div>

                            {correcting && (
                                <div data-review-form className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
                                    <div className="text-[11px] text-slate-500">
                                        分类只能选官方词表里的（二级随一级刷新）；材料名和颜色<b>可以留空</b>，
                                        留空就是沿用 AI 的值。填完按 <b>Enter</b> 提交。
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {/* value 是英文 —— 提交与存储一律英文, 只有显示是中文 */}
                                        <SearchSelect className="w-[180px]" value={finalL1} placeholder="选一级材质…" clearable
                                            onChange={(v) => { setFinalL1(v); setFinalL2(''); }}
                                            options={l1SelectOpts} />
                                        <SearchSelect className="w-[200px]" value={finalL2} disabled={!finalL1} placeholder="选二级材质…" clearable
                                            onChange={(v) => setFinalL2(v)}
                                            options={l2SelectOpts} />
                                        <input className="inp" style={{ width: 220 }} placeholder="材料名（可留空）…"
                                            value={finalMaterialName} onChange={(e) => setFinalMaterialName(e.target.value)} />
                                        {/* 原生取色器 + 手填框并排: 取色器方便从图上比对着调,
                                            手填框是为了能直接粘一个已知色值(取色器没法粘贴). */}
                                        <span className="inline-flex items-center gap-1.5">
                                            <input type="color" className="w-8 h-8 rounded border border-slate-200 bg-white p-0.5 cursor-pointer"
                                                value={/^#[0-9A-Fa-f]{6}$/.test(finalColorHex) ? finalColorHex : '#000000'}
                                                onChange={(e) => setFinalColorHex(e.target.value.toUpperCase())} />
                                            <input className="inp font-mono" style={{ width: 110 }} placeholder="#RRGGBB"
                                                value={finalColorHex} onChange={(e) => setFinalColorHex(e.target.value)} />
                                        </span>
                                        <input className="inp" style={{ width: 200 }} placeholder="备注（可留空）…" value={remark} onChange={(e) => setRemark(e.target.value)} />
                                        <button type="button" className="px-4 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40"
                                            disabled={submitting} onClick={submitCorrection}>提交修正 ⏎</button>
                                        <button type="button" className="px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100"
                                            onClick={() => setCorrecting(false)}>取消</button>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3 text-[11px] text-slate-400">
                                <span><b className="text-slate-600">← →</b> 上一条 / 下一条</span>
                                <span><b className="text-slate-600">1/3</b> 按下即提交并跳下一条</span>
                                <span><b className="text-slate-600">2</b> 改分类/材料名/颜色 · <b className="text-slate-600">Enter</b> 提交</span>
                                {/* 原来这儿有个文字版的「存疑 5」, 已提升成上面第 5 张卡片 ——
                                    同一个动作两个入口、两套样式, 迟早漂移. ml-auto 跟着挪到翻页按钮上 */}
                                <button type="button" className="ml-auto px-2.5 py-1 rounded-md text-slate-500 hover:bg-slate-100" onClick={() => go(-1)}>← 上一条</button>
                                <button type="button" className="px-2.5 py-1 rounded-md text-slate-500 hover:bg-slate-100" onClick={() => go(1)}>下一条 →</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
            </>}
        </div>
    );
}

export default ProductLabelReview;
